import { useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { useQuery as useReactQuery } from '@tanstack/react-query';

import { GET_EVENTS } from '../queries/events';
import { fetchLearnerCourseSchedules } from '../../pages/dashboard/api';

const eventCategories = {
  general: 'primary',
  meeting: 'gray',
  sprint: 'success',
  review: 'warning',
  testing: 'danger',
  planning: 'info',
};

export const useCalendarEvents = (filterUpcoming = false) => {
  const {
    data: customEvents,
    loading: customEventsLoading,
    error: customEventsError,
  } = useQuery(GET_EVENTS);

  const {
    data: courseSchedules,
    isLoading: courseSchedulesLoading,
    error: courseSchedulesError,
  } = useReactQuery({
    queryKey: ['learnerCourseSchedules'],
    queryFn: fetchLearnerCourseSchedules,
  });

  const events = useMemo(() => {
    const customScheduledEvents = (customEvents?.events || []).map((event) => ({
      title: event.name,
      start: event.startTime,
      end: event.endTime,
      variant: eventCategories[event.category],
      link: event.link,
      description: event.description,
    }));

    const courseSchedulesEvents = (courseSchedules || []).map((schedule) => ({
      title: (() => {
        if (schedule.assignment_type) {
          return schedule.title;
        }
        // The course-end badge ("Ends in N days") already conveys "Course ends",
        // so the title is just the course name to avoid repeating it.
        if (schedule.date_type === 'course-end-date') {
          return schedule.course_display_name;
        }
        return `${schedule.course_display_name} - ${schedule.title}`;
      })(),
      start: schedule.date,
      end: schedule.date,
      variant: schedule.assignment_type ? 'danger' : 'info',
      link: schedule.link,
      description: schedule.description,
      date_type: schedule.date_type,
      assignment_type: schedule.assignment_type,
      course_display_name: schedule.course_display_name,
      linkText: schedule.course_display_name,
      courseName: schedule.course_display_name,
      // Enrichment fields (all optional; undefined => current behaviour).
      complete: schedule.complete,
      learnerHasAccess: schedule.learner_has_access,
      ctaLabel: schedule.link_text,
      sectionName: schedule.section_display_name,
      hasOverride: schedule.has_override,
      overrideReason: schedule.override_reason,
      isRelease: schedule.is_release,
    }));

    let allEvents = [...customScheduledEvents, ...courseSchedulesEvents];

    if (filterUpcoming) {
      const now = new Date();
      allEvents = allEvents
        .filter((event) => event.start && new Date(event.start) >= now)
        .sort((a, b) => new Date(a.start) - new Date(b.start));
    }

    return allEvents;
  }, [customEvents, courseSchedules, filterUpcoming]);

  return {
    events,
    loading: customEventsLoading || courseSchedulesLoading,
    error: customEventsError || courseSchedulesError,
  };
};

export default useCalendarEvents;
